import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

interface ActivityLogEntry {
  id: string;
  tenantId: string;
  category: string;
  action: string;
  actor: string;
  details: Record<string, unknown>;
  createdAt: string;
}

interface DataStats {
  profiles: number;
  skills: number;
  assignments: number;
  matches: number;
  integrations: number;
}

interface HealthCheck {
  status: string;
  version: string;
  checks: Record<string, boolean>;
}

export function useHealthCheck() {
  return useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await fetch('/api/admin/health');
      return res.json() as Promise<{ success: boolean; data: HealthCheck; error: string | null }>;
    },
    refetchInterval: 30000
  });
}

export function useDataStats() {
  return useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => apiFetch<DataStats>('/admin/stats')
  });
}

export function useActivityLogs(limit = 50) {
  return useQuery({
    queryKey: ['admin-activity', limit],
    queryFn: () => apiFetch<ActivityLogEntry[]>(`/admin/activity?limit=${limit}`)
  });
}

export function useSyncAssignments() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ runId: string; status: string }>('/admin/sync-assignments', {
        method: 'POST',
        body: JSON.stringify({ sources: ['verama', 'upgraded'] })
      })
  });
}

export interface LocationFilter {
  regions: string[];
  counties: string[];
}

export function useLocationFilter() {
  return useQuery({
    queryKey: ['location-filter'],
    queryFn: () => apiFetch<LocationFilter>('/admin/location-filter')
  });
}

export function useSaveLocationFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filter: LocationFilter) =>
      apiFetch<null>('/admin/location-filter', {
        method: 'POST',
        body: JSON.stringify(filter)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-filter'] });
    }
  });
}

export function useClearLocationFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<null>('/admin/location-filter', { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-filter'] });
    }
  });
}

export interface AiConfigModel {
  id: string;
  name: string;
  description: string;
  provider: 'gemini' | 'zai';
}

export interface AiConfig {
  models: Record<string, string>;
  defaults: Record<string, string>;
  available: AiConfigModel[];
}

export function useAiConfig() {
  return useQuery({
    queryKey: ['ai-config'],
    queryFn: () => apiFetch<AiConfig>('/admin/ai-config')
  });
}

export function useSaveAiConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: Record<string, string>) =>
      apiFetch<null>('/admin/ai-config', {
        method: 'POST',
        body: JSON.stringify(config)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
    }
  });
}

export function useResetAiConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<null>('/admin/ai-config', { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
    }
  });
}

export interface TenantUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

export function useTenantUsers() {
  return useQuery({
    queryKey: ['tenant-users'],
    queryFn: () => apiFetch<TenantUser[]>('/admin/users')
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      apiFetch<null>(`/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
    }
  });
}

export interface RebuildMatrixResult {
  memberCount: number;
  totalSkills: number;
  gaps: number;
  categories: number;
}

export function useRebuildMatrix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<RebuildMatrixResult>('/admin/rebuild-matrix', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-profile'] });
      queryClient.invalidateQueries({ queryKey: ['competence-matrix'] });
    }
  });
}

export type { ActivityLogEntry, DataStats, HealthCheck };
